// TODO(pascal): Solve the multiple import of store.
// oxlint-disable-next-line no-unassigned-import
import '@store/store'

import { RegistrationControllerApiFactory } from '@api-client/api'
import { Loading } from '@components'
import { useWithAmpli } from '@contexts/AmpliContextProvider'
import {
	retryUnlessSessionRejected,
	TOKEN_REFRESH_RETRY_DELAYS_MS,
} from '@helpers/authRetry'
import { createLoginUrl } from '@helpers/urls'
import { useAsyncApi } from '@hooks/useAsyncApi'
import Mutex from '@services/asyncMutex'
import authService from '@services/authService'
import axios from 'axios'
import { createContext, useCallback, useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
interface UserInfo {
	id?: string
}

interface AuthContextValues {
	logout: () => void
	user: UserInfo
}

const noOp = () => void 0

const defaultAuthContextValues: AuthContextValues = {
	logout: noOp,
	user: {},
}

export const AuthContext = createContext<AuthContextValues>(
	defaultAuthContextValues
)
const mutex = new Mutex()
const refreshMutex = new Mutex()

const AuthContextProvider = () => {
	const [user, setUser] = useState<UserInfo>({})
	const [isAuthenticated, setIsAuthenticated] = useState(
		authService.isAuthenticated()
	)
	const [isRefreshingAuthToken, setIsRefreshingAuthToken] = useState(
		!authService.isAuthenticated() && !!authService.getRefreshToken()
	)
	const withAmpli = useWithAmpli()
	const navigate = useNavigate()
	const location = useLocation()

	const registrationControllerApi = useAsyncApi(
		RegistrationControllerApiFactory
	)

	const refreshToken = useCallback(async () => {
		const token = authService.getRefreshToken()
		const release = await refreshMutex.acquire()
		try {
			if (authService.isAuthenticated()) {
				return authService.getAccessToken()
			}

			if (token) {
				authService.clearAccessToken()
				const refreshTokenResponse = await retryUnlessSessionRejected(
					() =>
						registrationControllerApi(api =>
							api.refreshToken({ refreshToken: token })
						),
					TOKEN_REFRESH_RETRY_DELAYS_MS
				)
				if (refreshTokenResponse?.data) {
					authService.storeToken(refreshTokenResponse.data)
					setIsAuthenticated(authService.isAuthenticated())
					return refreshTokenResponse.data.access_token
				}
				// If refresh API call succeeded but returned no data, mark as not authenticated
				setIsAuthenticated(false)
				return undefined
			}
			// If no refresh token available, mark as not authenticated
			setIsAuthenticated(false)
			return undefined
		} catch {
			// If refresh API call failed, mark as not authenticated
			setIsAuthenticated(false)
			return undefined
		} finally {
			release()
		}
	}, [registrationControllerApi])

	const logoutAndReturnToHome = useCallback(() => {
		authService.logout()
		// Capture current path as destination for post-login redirect
		const currentPath = location.pathname + location.search
		// Only capture destination if not already on login or logout page
		const destination =
			currentPath === '/login' || currentPath === '/logout'
				? undefined
				: currentPath
		const loginUrl = createLoginUrl(destination)
		navigate(loginUrl)
	}, [navigate, location])

	useEffect(() => {
		if (!authService.isAuthenticated() && authService.getRefreshToken()) {
			refreshToken().finally(() => setIsRefreshingAuthToken(false))
		}
	}, [refreshToken])

	useEffect(() => {
		if (!isAuthenticated && !isRefreshingAuthToken) {
			logoutAndReturnToHome()
		}
	}, [isAuthenticated, isRefreshingAuthToken, logoutAndReturnToHome])

	useEffect(() => {
		try {
			const updateUserWithIdToken = () => {
				const idToken = authService.getIdTokenClaims()
				setUser({ id: idToken?.sub })
			}
			updateUserWithIdToken()

			const requestInterceptor = axios.interceptors.request.use(
				async config => {
					const release = await mutex.acquire()
					try {
						const token = authService.getAccessToken()

						if (config.headers && token) {
							config.headers['Authorization'] = `Bearer ${token}`
						}

						withAmpli(ampli => {
							const deviceId = ampli.client.getDeviceId()
							const sessionId = ampli.client.getSessionId()
							if (deviceId && config.headers && sessionId) {
								config.headers['X-Device-ID'] = deviceId
								config.headers['X-Session-ID'] = sessionId
							}
						})
					} finally {
						release()
					}

					return config
				}
			)

			const responseInterceptor = axios.interceptors.response.use(
				response => response,
				async error => {
					const originalRequest = error.config
					const isAuthError =
						error.response?.status === 401 || error.response?.status === 403
					const isRefreshRequest = originalRequest.url?.includes(
						'/registration/users/refresh'
					)
					if (isAuthError && !originalRequest._retry && !isRefreshRequest) {
						originalRequest._retry = true
						try {
							const accessToken = await refreshToken()
							if (accessToken) {
								originalRequest.headers.Authorization = `Bearer ${accessToken}`
								return await axios(originalRequest)
							}
							// If refresh failed to return a token, logout
							logoutAndReturnToHome()
							throw error
						} catch (refreshError) {
							// If refresh call itself failed, logout
							logoutAndReturnToHome()
							throw refreshError
						}
					}

					if (isAuthError) {
						logoutAndReturnToHome()
					}
					throw error
				}
			)

			return () => {
				axios.interceptors.request.eject(requestInterceptor)
				axios.interceptors.response.eject(responseInterceptor)
			}
		} catch {
			// Ignore errors.
		}
	}, [logoutAndReturnToHome, withAmpli, navigate, refreshToken])

	if (isRefreshingAuthToken || !isAuthenticated) {
		return <Loading noWrapper={false} containerHeight="100vh" />
	}

	return (
		<AuthContext.Provider
			value={{ logout: isAuthenticated ? logoutAndReturnToHome : noOp, user }}
		>
			<Outlet />
		</AuthContext.Provider>
	)
}

export default AuthContextProvider
